import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'May 19, 2026';
const CONTACT_EMAIL = 'extractstudy@proton.me';

const Privacy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | ExtractStudy';
  }, []);

  return (
    <div className="min-h-screen app-shell px-4 py-10 transition-colors duration-300">
      <div className="pointer-events-none fixed inset-0 opacity-65 mix-blend-screen" style={{ background: 'radial-gradient(60% 50% at 20% 30%, rgba(139,208,255,0.10), transparent), radial-gradient(45% 40% at 80% 20%, rgba(74,163,255,0.08), transparent)' }}></div>

      <div className="relative mx-auto max-w-2xl">
        <Link to="/" className="text-[var(--muted)] text-sm hover:text-[var(--text)] transition-colors">
          ← ExtractStudy
        </Link>

        <h1 className="section-title brand-title text-3xl sm:text-4xl font-bold mt-4 mb-2">
          Privacy Policy
        </h1>
        <p className="text-[var(--muted)] text-sm mb-8">Last updated: {LAST_UPDATED}</p>

        <div className="glass-panel rounded-2xl border border-[var(--border)] p-6 sm:p-8 space-y-6 text-[var(--text)] text-sm leading-relaxed">
          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">1. Introduction</h2>
            <p>
              ExtractStudy ("we", "us", "the service") is an AI-powered vocabulary
              flashcard generator available at <a href="https://www.extractstudy.com" className="text-[var(--accent)] hover:underline">www.extractstudy.com</a>.
              This policy explains what information we collect, how we use it, and the
              choices you have. By using ExtractStudy you agree to this policy.
            </p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">2. Information We Collect</h2>
            <p className="mb-2"><strong>Account information.</strong> If you create an account we store the email
            address you sign up with, a securely hashed password, and the first and last name
            you provide. If you sign in with Google we receive your name, email address, and
            profile picture from your Google account — we do not access any other Google data.</p>
            <p className="mb-2"><strong>Content you submit.</strong> Passages of text and photos you upload to
            generate flashcards are sent to our AI provider, processed in real time, and not
            retained after the response is returned. Generated flashcards are kept only for
            the duration of the page session unless you are logged in and they are saved to
            your Vocabulary Book.</p>
            <p className="mb-2"><strong>Vocabulary Book content.</strong> Cards you save (word, translation, type,
            optional example, language) are stored against your account so you can access
            them later. You can edit or delete any card at any time.</p>
            <p><strong>Usage data.</strong> We use Google Analytics 4 and Vercel Analytics to count
            page views and a small number of feature events (e.g. that a flashcard was
            generated). This is aggregate analytics — we do not link it to your account.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">3. How We Use Your Information</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>To generate flashcards and translations from the content you submit.</li>
              <li>To create and authenticate your account and keep you signed in.</li>
              <li>To save and display your Vocabulary Book entries.</li>
              <li>To understand how the product is used in aggregate and improve it.</li>
              <li>To respond when you contact us.</li>
            </ul>
            <p className="mt-2">We do not sell your data and we do not use your submitted content to train AI models.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">4. Third-Party Services</h2>
            <p>To deliver the service, ExtractStudy relies on:</p>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li><strong>Supabase</strong> — authentication and database hosting for accounts and Vocabulary Book.</li>
              <li><strong>OpenRouter / underlying AI models</strong> — to generate flashcards from your text and images.</li>
              <li><strong>Vercel</strong> — website hosting and Vercel Analytics.</li>
              <li><strong>Google Analytics 4</strong> — aggregate usage analytics.</li>
              <li><strong>Google Sign-In (optional)</strong> — only if you choose to sign in with Google.</li>
            </ul>
            <p className="mt-2">Each provider has its own privacy policy that governs how it processes data on our behalf.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">5. Cookies & Local Storage</h2>
            <p>We use cookies and browser local storage to keep you signed in, remember
            preferences (such as your selected language), and for analytics. You can clear
            these at any time through your browser settings; doing so will log you out.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">6. Data Retention</h2>
            <p>Submitted text and images are not retained after processing. Account
            information and Vocabulary Book entries are kept until you delete your account
            or the specific entries. Aggregate analytics data may be retained per the
            provider's retention policy.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">7. Your Rights</h2>
            <p>You can edit or delete any Vocabulary Book entry from the app. To delete
            your account and all associated data, email us at the address below and we will
            remove it. Where applicable laws give you additional rights (access, portability,
            objection), you may exercise them by contacting us.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">8. Children</h2>
            <p>ExtractStudy is not directed at children under 13. If you believe a child has
            created an account, contact us and we will remove it.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">9. Changes</h2>
            <p>We may update this policy from time to time. Material changes will be reflected
            in the "Last updated" date above. Continued use of ExtractStudy after a change
            constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">10. Contact</h2>
            <p>Questions about this policy can be sent to{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--accent)] hover:underline">
                {CONTACT_EMAIL}
              </a>.
            </p>
          </section>
        </div>

        <div className="mt-6 text-center">
          <Link to="/terms" className="text-[var(--muted)] text-sm hover:text-[var(--text)] transition-colors mr-4">
            Terms of Service
          </Link>
          <Link to="/" className="text-[var(--muted)] text-sm hover:text-[var(--text)] transition-colors">
            Back to ExtractStudy
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
